import { defineStore } from 'pinia'
import { markRaw } from 'vue'
import { nanoid } from 'nanoid'
import { useUserStore } from '@/authentication/user.store'
import { createTask, runAgent } from '@/search/agentic_search_api'
import type { AgentMessage, AgentRun } from '@/search/agentic_search_api'
import { searchAssessmentsByTitle } from '@/search/algolia_search'

export const MIN_RELEVANCE_SCORE = 7

export const ASSESSMENT_SEARCH_PROMPT = `(Assessments Only)

ADDITIONAL NOTES:
- If the query is short with minimal context, treat the request as a keyword search for assessments.
- When providing an assessment rationale, don't reference other assessments.

CONSTRAINTS:
- Don't include assessments about braille UNLESS the SEARCH REQUEST specifically mentions the student is blind / low vision.
`

const AGENT_CONFIG = { model: 'gpt-4', temperature: 0.7 }
const SIBLING_SET_OMITTED_FIELDS = ['content_alignments', 'domains']

export interface AgenticSearchFilters {
  studentName?: string
  gradeLevels?: string[]
  subject?: string
  category?: string
}

export interface AgenticSearchRequest {
  query: string
  files?: File[]
  filters?: AgenticSearchFilters
  searchGrade?: string
}

export interface AgentContent {
  uuid: string
  title: string
  type: string
  relevance_score?: number
  rationale?: string
  set_number?: number
  grades?: string[]
  [field: string]: unknown
}

export interface AgentEvent {
  type: string
  data?: unknown
  timestamp: string
}

interface LatestSearch {
  prompt: string | null
  searchId: string | null
  searchGrade: string | null
  inputTokens: number | null
  outputTokens: number | null
  files: File[]
}

type SearchCompleteCallback = () => unknown

// Bumped by every clear. A search whose task comes back after a clear — the user left the
// page, or started another search — must not start streaming into the state that replaced it.
let searchGeneration = 0

function emptyLatestSearch(): LatestSearch {
  return { prompt: null, searchId: null, searchGrade: null, inputTokens: null, outputTokens: null, files: [] }
}

export const useAgenticSearchStore = defineStore('agenticSearch', {
  state: () => ({
    events: [] as AgentEvent[],
    loading: false,
    error: null as string | null,
    currentTaskId: null as string | null,
    latestSearch: emptyLatestSearch(),
    run: null as AgentRun | null,
    searchCompleteCallbacks: markRaw([]) as SearchCompleteCallback[]
  }),

  getters: {
    contentResults(state): AgentContent[] {
      return state.events
        .filter((event) => event.type === 'content_generated')
        .map((event) => event.data as AgentContent | undefined)
        .filter((content): content is AgentContent => (content?.relevance_score ?? 0) >= MIN_RELEVANCE_SCORE)
        .sort((a, b) => (b.relevance_score ?? 0) - (a.relevance_score ?? 0))
    },
    assessmentResults(): AgentContent[] {
      return this.contentResults
        .filter((content) => content.type === 'assessment')
        .sort((a, b) => (b.relevance_score ?? 0) - (a.relevance_score ?? 0) || (a.title ?? '').localeCompare(b.title ?? ''))
    },
    hasAssessmentResults(): boolean {
      return this.assessmentResults.length > 0
    },
    currentPhase(state): string | null | undefined {
      const phaseEvents = state.events.filter((event) => event.type === 'phase_started')
      if (!phaseEvents.length) {
        return null
      }
      return (phaseEvents[phaseEvents.length - 1].data as { phase?: string } | undefined)?.phase
    },
    latestTokenUsage(state) {
      return { inputTokens: state.latestSearch.inputTokens, outputTokens: state.latestSearch.outputTokens }
    }
  },

  actions: {
    async searchAssessments(request: AgenticSearchRequest): Promise<string> {
      return this.search(request, { addlPrompt: ASSESSMENT_SEARCH_PROMPT, onComplete: () => this.searchAssessmentSets() })
    },

    // The completion callback is registered before the agent starts streaming, so a fast
    // stream cannot finish before it is in place.
    async search({ query, files = [], filters, searchGrade }: AgenticSearchRequest, { addlPrompt, onComplete }: { addlPrompt?: string, onComplete?: SearchCompleteCallback } = {}): Promise<string> {
      this.clearSearchAndResults()
      const generation = searchGeneration
      this.loading = true
      const searchId = nanoid()
      this.latestSearch = { ...emptyLatestSearch(), prompt: query, searchId, searchGrade: searchGrade ?? null, files }
      if (onComplete) {
        this.addSearchCompleteCallback(onComplete)
      }

      try {
        const prompt = addlPrompt ? `${query} ${addlPrompt}` : query
        const { task_id: taskId } = await createTask(buildSearchFormData(prompt, files, filters, userStateForSearch()))
        if (generation !== searchGeneration) {
          return searchId
        }
        this.currentTaskId = taskId
        this.run = markRaw(runAgent(taskId, {
          onMessage: (message) => this.handleAgentMessage(taskId, message),
          onError: (error) => {
            if (this.currentTaskId === taskId) {
              this.error = error.message || 'An error occurred during search'
              this.loading = false
            }
          },
          onComplete: () => {
            if (this.currentTaskId === taskId) {
              this.loading = false
            }
          }
        }))
        return searchId
      } catch (e) {
        if (generation === searchGeneration) {
          this.error = (e instanceof Error && e.message) || 'Failed to start search'
          this.loading = false
          this.searchCompleteCallbacks.splice(0)
        }
        throw e
      }
    },

    handleAgentMessage(taskId: string, message: AgentMessage) {
      if (this.currentTaskId !== taskId) {
        return
      }
      const event = { ...message, timestamp: new Date().toISOString() }
      if (message.type === 'generation_complete') {
        const usage = message.data as { input_tokens?: number, output_tokens?: number } | undefined
        this.latestSearch.inputTokens = usage?.input_tokens ?? null
        this.latestSearch.outputTokens = usage?.output_tokens ?? null
      } else if (message.type === 'content_generated' || message.type === 'phase_started') {
        this.events.push(event)
      } else if (message.type === 'completed') {
        this.events.push(event)
        this.loading = false
        this.runSearchCompleteCallbacks()
      }
    },

    runSearchCompleteCallbacks() {
      const callbacks = this.searchCompleteCallbacks.splice(0)
      Promise.allSettled(callbacks.map((callback) => Promise.resolve().then(callback))).then((results) => {
        results
          .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
          .forEach((result) => console.error('Search complete callback failed:', result.reason))
      })
    },

    addSearchCompleteCallback(callback: SearchCompleteCallback) {
      this.searchCompleteCallbacks.push(callback)
    },

    // An assessment in a numbered set (e.g. "Reading Fluency Set 1") stands for the whole
    // set, so its siblings are looked up by title and added with the same relevance.
    async searchAssessmentSets() {
      const taskId = this.currentTaskId
      const setAssessments = this.assessmentResults.filter((assessment) => assessment.set_number)
      const lookups = await Promise.allSettled(
        setAssessments.map((assessment) => searchAssessmentsByTitle<AgentContent>(assessment.title, assessment.grades))
      )
      if (this.currentTaskId !== taskId) {
        return
      }

      const seenUuids = new Set(this.assessmentResults.map((assessment) => assessment.uuid))
      const siblingSets = setAssessments.flatMap((assessment, index) => {
        const lookup = lookups[index]
        if (lookup.status === 'rejected') {
          console.error(`Failed to search for assessment sets for "${assessment.title}":`, lookup.reason)
          return []
        }
        return lookup.value
          .filter((set) => {
            if (set.title !== assessment.title || seenUuids.has(set.uuid)) {
              return false
            }
            seenUuids.add(set.uuid)
            return true
          })
          .map((set) => ({
            ...omitFields(set, SIBLING_SET_OMITTED_FIELDS),
            type: 'assessment',
            relevance_score: assessment.relevance_score,
            rationale: assessment.rationale
          }))
          .sort((a, b) => (a.set_number ?? 0) - (b.set_number ?? 0))
      })

      const timestamp = new Date().toISOString()
      this.events.push(...siblingSets.map((set) => ({ type: 'content_generated', data: set, timestamp })))
    },

    clearResults() {
      searchGeneration++
      this.run?.close()
      this.run = null
      this.searchCompleteCallbacks.splice(0)
      this.events = []
      this.loading = false
      this.error = null
      this.currentTaskId = null
    },

    clearSearchAndResults() {
      this.clearResults()
      this.latestSearch = emptyLatestSearch()
    }
  }
})

function omitFields<T extends object>(object: T, fields: string[]): T {
  return Object.fromEntries(Object.entries(object).filter(([field]) => !fields.includes(field))) as T
}

// The agent tailors results to the user's state, falling back to their content profile.
function userStateForSearch(): string {
  const userStore = useUserStore()
  return userStore.eventMetadata?.ust || userStore.userContentProfileId || ''
}

function buildSearchFormData(prompt: string, files: File[], filters: AgenticSearchFilters | undefined, userState: string): FormData {
  const formData = new FormData()
  files.forEach((file) => formData.append('files[]', file))
  formData.append('config', JSON.stringify({ prompt, ...AGENT_CONFIG }))

  const fields: [string, string | undefined][] = [
    ['studentName', filters?.studentName],
    ['gradeLevel', filters?.gradeLevels?.join(',')],
    ['subject', filters?.subject],
    ['category', filters?.category],
    ['state', userState]
  ]
  fields.forEach(([name, value]) => {
    if (value) {
      formData.append(name, value)
    }
  })
  return formData
}
